import { EXTERNAL_ASSOCIATION } from '@engoo/view-model';
import { DISCARD_LOCAL_CHANGES, ENTITY_DELETED, SAVED_TO_SERVER, UPDATE_FROM_SERVER } from 'app/remote/generic/actions';
import { ENTITY_PREFIX, LOG_PREFIX } from 'app/remote/generic/constants';
import * as Reconcile from 'app/remote/generic/reconcile';
import Logger from 'app/services/logger';
import { Map } from 'immutable';
import { combineReducers } from 'redux-immutablejs';

export const makeDiscard = ({ prefix, vmClient, updateLocalMap }) => {
  const entityPrefixes = vmClient.getStorePrefixes();

  const getCache = (state, storePrefix) =>
    state.getIn([prefix, ENTITY_PREFIX, storePrefix]);

  const copyFromCache = (state, storePrefix) =>
    updateLocalMap(state, (local) => local.set(storePrefix, getCache(state, storePrefix) || Map()));

  return (state) => entityPrefixes.reduce(copyFromCache, state);
};

const makeSyncReducer = (dependencies) => {
  const discard = makeDiscard(dependencies);

  return (state, action) => {
    switch (action.type) {
      case UPDATE_FROM_SERVER:
      case SAVED_TO_SERVER:
      case ENTITY_DELETED:
      case DISCARD_LOCAL_CHANGES:
        return discard(state);

      default:
        return state;
    }
  };
};

export const makeRemoteEntityReducer = ({ vmClient }) => (state = Map(), action) => {
  const { payload, type } = action;

  switch (type) {
    case UPDATE_FROM_SERVER:
    case SAVED_TO_SERVER:
      try {
        if (payload.payload !== null && payload.payload !== undefined) {
          if (payload.type && payload.type.kind === EXTERNAL_ASSOCIATION) {
            return vmClient.updateExternalAssociation(
              state,
              payload.payload,
              payload.type,
              payload.id,
            );
          } else {
            return vmClient.update(state, payload.payload);
          }
        } else {
          return state;
        }
      } catch (e) {
        // In the event an error occurs, log the response.
        Logger.error('Error occured while parsing response from the server.', { payload });
        throw e;
      }

    case ENTITY_DELETED:
      if (payload.kind === EXTERNAL_ASSOCIATION) {
        // remove all instances of id from associations
        return state.update(payload.storePrefix, (associations) =>
          associations && associations.map((association) =>
            association.filterNot((id) => id === payload.id),
          ),
        );
      } else {
        return state.deleteIn([payload.storePrefix, payload.id]);
      }

    default:
      return state;
  }
};

export const makeRemoteStateReducer = (dependencies) => combineReducers({
  [LOG_PREFIX]:    Reconcile.logReducer,
  [ENTITY_PREFIX]: makeRemoteEntityReducer(dependencies),
});

export const makeStoreReducer = (dependencies) => (inner) => {
  const syncReducer    = makeSyncReducer(dependencies);
  const reconcileReducer = Reconcile.makeReconcileReducer(dependencies, inner);

  return (state, action) => {
    let newState = state;

    // Update all state (and remote database).
    newState = inner(newState, action);

    // Replace local database with remote database.
    newState = syncReducer(newState, action);

    // Replay actions.
    newState = reconcileReducer(newState, action);

    return newState;
  };
};
